import React, { useContext, useState, useEffect, FC } from 'react';

import { Grid, Paper, Chip } from '@material-ui/core';

import { GlobalContext } from '../../../../store';
import { IAdminClub } from '../../../../store/admin/clubs/store';
import { IAdminMembership } from '../../../../store/admin/memberships/store';

import AddPlayers from '../../../../components/AddPlayersCard';
import ManagePlayerGroups from '../../../../components/ManagePlayerGroups/ManagePlayerGroups';
import { Subheader } from '../../Common/Subheader/Subheader';
import { useRouteInfoState } from '../../../../components/RouteInfo/Context/RouteInfo.context';
import ClubPlayerRoster from './ClubPlayerRoster';
import { PlayersRosterStyled } from './PlayersRoster.Styles';
import Button from '../../../../components/Button';
import ClubPendingInvitations from './ClubPendingInvitations';
import { IClubInvitation } from '../../../../store/clubInvitations/store';

type TSections = 'roster' | 'invitations';

const PlayersRoster: FC = () => {
  const {
    actions: {
      adminMemberships: { getMemberships },
      adminGroups: { getGroups },
      clubInvitations: { getClubInvitations }
    },
    adminClubs,
    clubInvitations
  } = useContext(GlobalContext);

  const { clubId } = useRouteInfoState();

  const [selectedMembers, setSelectedMembers] = useState<IAdminMembership[]>([]);
  const [currentClub, setCurrentClub] = useState<IAdminClub>();
  const [selectedSection, setSelectedSection] = useState<TSections>('roster');
  const [pendingInvitations, setPendingInvitations] = useState<IClubInvitation[]>([]);

  useEffect(() => {
    if (clubId) {
      getClubInvitations(clubId).catch();
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId) {
      getMemberships(clubId, true).catch();
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId) {
      getGroups(clubId).catch();
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId && adminClubs.all[Number(clubId)]) {
      setCurrentClub(adminClubs.all[Number(clubId)]);
    }
  }, [clubId, adminClubs]);

  useEffect(() => {
    if (pendingInvitations.length === 0) {
      const invitations = clubInvitations.invitations.reduce(
        (acc: IClubInvitation[], invitation) => {
          return !!invitation && !invitation.accepted && !invitation.expired
            ? [...acc, invitation]
            : acc;
        },
        []
      );

      setPendingInvitations(invitations);
    }
  }, [clubInvitations]);

  const selectSection = (section: TSections) => () => {
    setSelectedSection(section);
  };

  const sections: Record<TSections, JSX.Element> = {
    invitations: <ClubPendingInvitations invitations={pendingInvitations} />,
    roster: (
      <ClubPlayerRoster selectedMembers={selectedMembers} setSelectedMembers={setSelectedMembers} />
    )
  };

  return (
    <div className="admin-layout">
      {currentClub && (
        <Subheader
          clubId={currentClub.id}
          profileImage={currentClub.profileImage}
          title="Players Roster"
        />
      )}

      <PlayersRosterStyled className="admin-wrapper">
        <Grid className="main-grid-container" container={true} direction="row">
          <Grid item={true} xs={4}>
            <AddPlayers
              headerText={'ADD NEW MEMBER(S)'}
              className="add-players-card--player-groups"
            />

            <ManagePlayerGroups selectedMembers={selectedMembers} />
          </Grid>

          <Grid item={true} xs={8}>
            <Paper elevation={1} className="paper player-list-paper override">
              <div className="section-selector">
                <Button
                  onClick={selectSection('roster')}
                  variant="text"
                  color="primary"
                  disabled={selectedSection === 'roster'}
                >
                  CLUB PLAYER ROSTER
                </Button>

                <Button
                  onClick={selectSection('invitations')}
                  variant="text"
                  color="primary"
                  disabled={selectedSection === 'invitations'}
                >
                  PENDING INVITES
                  <Chip
                    className="pending-chip"
                    color={selectedSection !== 'invitations' ? 'primary' : undefined}
                    label={pendingInvitations.length}
                    variant="outlined"
                  />
                </Button>
              </div>

              {sections[selectedSection]}
            </Paper>
          </Grid>
        </Grid>
      </PlayersRosterStyled>
    </div>
  );
};

export default PlayersRoster;
